/**
 * PROJECT-111A — Marked Feedback API (Phases 1–2)
 *
 * The fast loop: mark a span, get a better one in ~3 s, accept or decline.
 *
 * None of these calls touches /kernel/strategize, /kernel/patch or /kernel/apply-patch.
 * The `61,73,rerun` chain is frozen (D-001) and stays the escalation path — this is a
 * second, cheaper path beside it, not a replacement.
 *
 * Nothing here writes to a kernel. The verdict records an exchange; the kernel-learning
 * half (Phases 3–5) is not built yet and the BE says so via `learningQueued: false`.
 */

import AuthUtils from "@/providers/firebase/authUtils";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────

export interface ApiResponse<T = Record<string, unknown>> {
  status: boolean;
  statusCode: number;
  message: string;
  data: T | null;
}

/** One addressable field of a call's output, plus the kernels behind it. */
export interface AttributedField {
  field: string;
  text: string;
  length: number;
  /**
   * Content kernels that could have produced prose in this field.
   *
   * Honestly wide (~12 on a real assembly): nothing in the data maps a content kernel
   * to a field, so this is the candidate SET, not an answer. Narrowing it to one kernel
   * is the router's job in Phase 3.
   */
  candidateKernelKeys: string[];
  candidateCount: number;
  /** BD999 — owns the field's shape, not its prose. */
  shapeKernelKeys: string[];
  /** Deterministic: 1.0 when the span lies inside this field. No model self-assessment. */
  confidence: number;

  /**
   * T0811 — backward-fenced provenance, when the bot marked its own output.
   *
   * Each segment is a stretch of text and the kernels that wrote it — typically one or
   * two, against a field candidate set of ~12. Empty when the output carries no markers,
   * in which case the field axis is still the answer.
   *
   * ⚠️ These are a model's SELF-REPORT, validated against the call's assembly but not
   * verifiable beyond that. A marker naming a kernel this call never loaded is rejected
   * and listed in `unresolvedMarkers`, never resolved to a plausible neighbour.
   */
  segments: OutputSegment[];
  /** The original text with markers still in it. `text` above is always clean. */
  markedText?: string;
  markerCount?: number;
  /** Markers naming kernels outside this assembly — the model claimed something untrue. */
  unresolvedMarkers?: string[];
  /** Segments whose coordinate matched more than one kernel (T0102). */
  ambiguousCount?: number;

  /**
   * T0850 — stretches of this field that are exactly a supplied input value.
   *
   * Independent of `segments`: computed by string identity, so it is present even on a
   * bot that never marked its output at all. `withValueSpans` carves these out of the
   * segments before rendering, which is what makes a single name separately hoverable and
   * markable instead of being swallowed by the sentence around it.
   */
  valueSpans?: ValueSpan[];

  /**
   * T0855 — the COMPLETE tiling of this field, and the one to render when it is present.
   *
   * ⚠️ NOT the same list as `segments`, and it does not replace it. `segments` is the
   * MARKER artefact: present only where the model fenced, merged, and on a degenerate run
   * a single span over the whole field — which is what made a click on one name highlight
   * an entire email. This is every character in exactly one segment, attributed by the
   * strongest layer that covers it, with markers demoted to one layer among several.
   *
   * It needs neither the model to have cooperated nor `kernels.embedding` to be populated,
   * so it is the first attribution here that produces an answer on EVERY call. `segments`
   * stays because `locate_span` and the marker-quality view read it.
   */
  attributedSegments?: AttributedSegment[];
  /** How much of the delivered text is known exactly vs claimed vs fallen back on. */
  attributionCoverage?: {
    segments: number;
    chars: number;
    exactChars: number;
    markerChars: number;
    /** Share of characters attributed by string identity — a FACT, not a claim. */
    exactPct: number;
    /** Share attributed by anything better than the field's whole candidate set. */
    attributedPct: number;
  };
}

export interface OutputSegment {
  /** Offsets into the CLEAN text — the string the user actually reads and marks. */
  start: number;
  end: number;
  text: string;
  /** Raw markers as written, e.g. ["<BD10.5>", "<BD1.1>"]. */
  markers: string[];
  kernelKeys: string[];
  unresolvedMarkers: string[];
  /** coordinate → the several kernels it matched, when it was not unique. */
  ambiguous: Record<string, string[]>;

  /**
   * T0849 — kernelKey → share of this segment, summing to 1. Best-first.
   *
   * `kernelKeys` says WHICH kernels; this says HOW MUCH. It exists because a flat set
   * cannot separate two sentences that cite the same kernels — `raisy_05516` fenced six
   * sentences with an identical four-kernel set and a click on one name selected the whole
   * email. Scores differ per sentence even when the sets do not.
   *
   * ⚠️ SEMANTIC PROXIMITY, NOT MEASURED CAUSATION. Never present this as "BD1 caused 71%
   * of this sentence". It ranks; it does not measure. Absent when the kernels have no
   * stored embedding, or for a `sole` candidate (nothing was embedded, so there is no
   * number to report — D-028).
   *
   * 🚨 D-028 — A PER-KERNEL COSINE IN [0, 1], NOT A SHARE. These do NOT sum to 1 and must
   * never be rendered with a `%`. Each is that kernel's own similarity to the segment,
   * independent of which other kernels are candidates — so it does not move when another
   * kernel is marked, and it IS comparable across segments and across output versions.
   */
  kernelScores?: Record<string, number>;
  kernelScoresMeta?: {
    /** Candidates were within 0.03 cosine — the ORDER was WITHHELD. Values still present. */
    flat?: boolean;
    /** Cited kernels with no stored embedding. Excluded from the ranking, NOT scored 0. */
    unscoredKernelKeys?: string[];
    spread?: number;
    semanticOnly?: boolean;
  };

  /**
   * T0868 — this span was REWRITTEN on a composed `v+1`, so its kernels are inherited.
   *
   * 🚨 The tooltip's *"The bot reported these kernels itself"* is false here and true
   * everywhere else on the same row. The span rewriter emits no `<K:>` markers, so
   * `_compose_marked` fences its output with the PARENT span's candidates — the claim is
   * one version old, about different words that stood in the same place.
   *
   * Set client-side by `resolveSegments` from the attribution's `rewrittenSpans`. Absent
   * on every non-composed row, and on composed rows written before T0868 recorded offsets.
   */
  inheritedProvenance?: boolean;
  /**
   * T0850 — this sub-span IS a supplied input value, matched by string identity.
   *
   * Set by `withValueSpans` when carving a value out of its parent segment. Unlike every
   * other attribution in this file it is a FACT, not a claim: the value was supplied, the
   * value is in the output, and this is where it is.
   */
  exactValue?: boolean;
  /** Which declared input this span is — e.g. `RECIPIENT_NAME`. */
  valueOf?: string;
  /**
   * T0850b — this span is an UNFILLED slot (`[START_DATE]`), not a supplied value.
   *
   * Same certainty, opposite direction: the BD0 contract declared the slot and nothing
   * ever filled it, so the text is definitionally BD0's. ⚠️ The UI must not describe it
   * as "the supplied value" — nothing was supplied, and that is the whole point. It also
   * marks a real defect the reader can see, which makes it the most actionable span on
   * the page.
   */
  unfilled?: boolean;
  /**
   * T0855 — how this segment's kernels were decided, once `resolveSegments` has
   * normalised a complete tiling into this shape. Absent on raw marker segments.
   */
  method?: AttributionMethod;
  /** T0855 — what this text is structurally. An annotation, never an attribution. */
  role?: SegmentRole;
}

/**
 * T0850 — a stretch of output that is exactly a value BD0 supplied.
 *
 * ⚠️ THE ONLY NON-CLAIM IN THIS API. A marker is the model's self-report, `raisy_62`'s
 * pick is another model's opinion, and a reliance score is proximity — all three can be
 * confidently wrong. A value span cannot: those characters ARE that supplied value.
 *
 * It answers 👤's *"when I tag a name in an email it should come under BD0"* — pointing
 * inside one resolves to the input kernel alone, with the router skipped entirely.
 */
/**
 * T0855 — HOW a segment's kernels were decided. This is the field that makes the hover
 * card trustworthy rather than one more opinion.
 *
 *   value        this text IS a supplied input value        FACT
 *   placeholder  an unfilled `[SLOT]` the contract declared  FACT
 *   marker       the model said so                          CLAIM
 *   fallback     the field's whole candidate set            the floor
 *
 * ⚠️ The first two are the only attributions in this system entitled to be shown as
 * certain. Presenting a `marker` or `fallback` segment the same way would put a claim and
 * a fact behind the same words, which is the thing D-021's honesty limit is about.
 */
export type AttributionMethod = "value" | "placeholder" | "marker" | "fallback";

/** What a stretch of text IS structurally. An annotation — never an attribution. */
export type SegmentRole = "greeting" | "body" | "closing" | "signature";

/** One tile of the complete segmentation. Tiles are contiguous, ordered and gapless. */
export interface AttributedSegment {
  start: number;
  end: number;
  text: string;
  kernelKeys: string[];
  method: AttributionMethod;
  /** True only for `value` and `placeholder`. */
  exact: boolean;
  role: SegmentRole;
  kernelScores?: Record<string, number> | null;
  /**
   * T0861 — the caveat that travels WITH `kernelScores`, and must never be dropped.
   *
   * 🚨 `flat: true` is the scorer REFUSING to rank (candidates within 0.03 cosine).
   * Without this field `orderedKernels` sorts on noise and `MarkableOutput` numbers the
   * rows 1./2./3., so a withheld ranking renders as a confident one. Verified by
   * executing both paths.
   *
   * ⚠️ D-028 — WHAT `flat` SUPPRESSES CHANGED. It used to overwrite the scores with equal
   * shares, so the FE blanked the numbers entirely; scores are now per-kernel cosines,
   * which are the real measurement whether or not they can be ordered. `flat` therefore
   * drops the ORDER (no sort, no 1./2./3.) and keeps the VALUES — "these are all about
   * equally related" is the one state the old share-based display could never express.
   *
   * `sole: true` means a single candidate: no embedding was spent, so `kernelScores` is
   * empty by design rather than missing. Never render a number for it.
   */
  kernelScoresMeta?: {
    flat?: boolean;
    sole?: boolean;
    unscoredKernelKeys?: string[];
    semanticOnly?: boolean;
  } | null;
  /** Which declared input this is, when `method` is `value` or `placeholder`. */
  valueOf?: string | null;
  /** Set when the slot was never filled — the text reached the reader as `[LIKE_THIS]`. */
  unfilled?: boolean | null;
}

export interface ValueSpan {
  /** Offsets into the CLEAN text, same coordinate space as `OutputSegment`. */
  start: number;
  end: number;
  text: string;
  /** Always the one BD0 kernel that declared this input. */
  kernelKeys: string[];
  /** The declared input name — lets the UI say "RECIPIENT_NAME", not just "BD0". */
  valueOf: string;
  exact: true;
  /** Set when this is an unfilled `[SLOT]` rather than a supplied value. */
  unfilled?: boolean;
}

/**
 * T0800 — one BD tier of the assembly, grouped and labelled from the bot index.
 *
 * The second axis a mark can be aimed along. The field axis asks "where in the output
 * is this?"; this asks "which MATTER is wrong?" — and a matter like Tone is not only in
 * `BD120_Tone`, it is also constrained by the input contract (BD0) and by Capability
 * (BD10). Those two carry `role: "defining"` precisely because they shape the matter
 * without writing any prose themselves (IN-11).
 */
export interface AttributedDimension {
  /** BD tier, e.g. 120. `null` for kernels whose key carries no tier at all. */
  dimension: number | null;
  /** "BD120_Tone" — the index block this came from, or null when the index was absent. */
  indexKey: string | null;
  /** "Tone". Falls back to a bare "BD120" rather than inventing a name. */
  name: string;
  /** "How does it sound?" — the index's own question for this dimension. */
  question: string;
  /**
   * content    — writes prose into the output
   * defining   — BD0 / BD10: constrains the content without writing it
   * shape      — BD999: owns the output's structure
   * structural — BD998 and untiered keys
   */
  role: "content" | "defining" | "shape" | "structural";
  kernelKeys: string[];
  labels: Record<string, string>;
}

export interface CallAttribution {
  callId: string;
  botId: string;
  /**
   * "marker"    — the bot fenced its own output, so a span resolves to the one or two
   *               kernels that wrote it rather than the field's ~12 candidates (T0811).
   * "field"     — spans can be marked.
   * "signature" — the output has no structure, so marking is impossible and feedback
   *               takes the unmarked path (D-005). A real answer, not a degraded one.
   */
  resolution: "field" | "signature" | "marker";
  fields: AttributedField[];
  signatureKernelKeys: string[];
  contentKernelKeys: string[];
  shapeKernelKeys: string[];
  structuralKernelKeys: string[];
  /** T0800 — every BD tier in the assembly, for tagging a mark by matter. */
  dimensions: AttributedDimension[];
  /** kernelKey → "BD120.2 Tone". Display only — the mark always carries the kernelKey. */
  labels: Record<string, string>;
  /**
   * T0868 — spans on a composed `v+1` whose kernels are INHERITED from the parent version
   * rather than self-reported. Empty or absent on every row that is not a composed one.
   *
   * Offsets index the CLEAN field text, the same coordinate space as `attributedSegments`.
   */
  rewrittenSpans?: Array<{
    field: string;
    start: number;
    end: number;
    rewriteCallId?: string | null;
  }>;
  note: string;
}

/**
 * T1366 — the deterministic coherence verdict on a proposal.
 *
 * `D-135-003`: the rules decide (no model), and only this half may object. It is an
 * OBJECTION, never a refusal — `T1367` requires the reader can always accept.
 */
export interface CoherenceVerdict {
  /** Whether anything was found. */
  objection: boolean;
  /** `"blocking"` for a purpose-bearing loss (a date, an amount); `"none"` when quiet. */
  severity: "blocking" | "advisory" | "none";
  /** A sentence naming what was lost, written to be read by the person accepting. */
  reason: string;
  /** The anchors the proposal drops, each with its class (`temporal` / `quantitative`). */
  anchors: Array<{ class: string; token: string }>;
}

export interface SpanRewriteResult {
  exchangeId: string;
  exchangeVersion: number;
  rewriteCallId: string | null;
  parentCallId: string;
  field: string;
  originalSpan: string;
  rewrittenSpan: string;
  /**
   * T1366 — computed server-side on every proposal, so the card can object INLINE beside the
   * change rather than behind a button (`D-135-008`: a check the reader must remember to press
   * is a check that will not be used). Null/absent on responses predating the wiring.
   */
  coherence?: CoherenceVerdict | null;
  spanStart: number;
  spanEnd: number;
  /**
   * Narrowed to the tagged dimension's kernels when a `dimension` was sent (T0803), or
   * to the marked segment's kernels when the bot fenced its output (T0811).
   */
  candidateKernelKeys: string[];
  /** Set when markers narrowed it — the bot's own claim about what wrote this span. */
  markerKernelKeys?: string[];
  markerSegmentCount?: number;
  /** The span crosses a fence, so it genuinely has more than one author. */
  spansMultipleSegments?: boolean;
  /** The dimension this mark was tagged with, echoed back. Null when untagged. */
  dimension: AttributedDimension | null;
  /** Set when the tag named a tier this call's assembly does not contain. Never silent. */
  dimensionUnavailable?: number | null;
  /** The number T0301's p90 ≤ 3 s target is measured on. */
  latencyMs: number;
}

/**
 * T0804 / D-014 — the `v+1` output.
 *
 * `modelInvoked` is always false and is on the response on purpose: this is the parent's
 * output with the accepted spans substituted at their recorded offsets, NOT a re-run. A
 * re-run regenerates every field, so it could not produce "the previous output with this
 * sentence fixed" — which is the thing that was asked for.
 *
 * The row is written under the ORIGINAL bot, so it appears in the call history that
 * `CompareOutputsModal` already reads and can be diffed against its parent with no new
 * component (D-016).
 */
export interface ComposedVersion {
  composedCallId: string;
  parentCallId: string;
  botId: string;
  /** v_max + 1 across this output's lineage — chains and branches both raise the floor. */
  outputVersion: number;
  appliedSpans: {
    field: string;
    rewriteCallId: string | null;
    exchangeId: string;
    from: string;
    to: string;
    dimension: number | null;
    dimensionName: string | null;
    /**
     * T1354 — `user_filled` when a person typed this value into a `[SLOT]`, null when a
     * model rewrote the span. The two travel one list because they land in one version;
     * they are not the same act and the UI must not describe them the same way.
     */
    source?: "user_filled" | null;
    /** The `[SLOT]` this replaced, when it was a fill. */
    slot?: string | null;
    /** T1354 — the BD0 kernel that declared the slot. */
    ownerKernelKey?: string | null;
  }[];
  fieldsChanged: string[];
  /** Ties this version to the background kernel run (T0806), when one was started. */
  patchRunId: string | null;
  /** True when the 62→73 fan-out was queued. It proposes; it writes nothing (D-017). */
  chainStarted?: boolean;
  /**
   * T1354 — the values this version filled in, in the order they were applied.
   *
   * A fill is NOT a defect, so it is deliberately absent from `appliedSpans`' learning
   * half — nothing here is sent to the kernel-fix chain. A fill is a one-off per-call
   * fact (D-135-018): it lands in the draft and is NOT remembered.
   */
  filledSlots?: {
    slot: string | null;
    value: string | null;
    field: string | null;
    ownerKernelKey: string | null;
    exchangeId: string | null;
  }[];
  output: Record<string, unknown> | string;
  modelInvoked: false;
}

/**
 * What the invisible learning half actually did. Every field is reported, including the
 * ways it declined to write: D-002 shows the user nothing at write time, so a refusal
 * nobody can see is indistinguishable from a success (R-01c).
 */
export interface LearningOutcome {
  attempted: boolean;
  verdict: string;
  /** True ONLY when a memory line actually landed on a kernel. */
  written: boolean;
  /** Why nothing was written, when nothing was. Always set unless `written`. */
  reason: string | null;
  kernelKey?: string;
  confidence?: number;
  /** Router was not confident enough for anything to be written on its answer. */
  belowFloor?: boolean;
  /**
   * D-019 — this exchange carries what the single learning caller needs (feedback +
   * candidate kernels), so the chain can route it. A ROUTING fact about the exchange.
   *
   * ⚠️ Renamed from `deferredToChain`, which also encoded the deployment posture and so
   * meant two things at once (RULES_NAMING §4). The posture now has its own field below.
   */
  chainEligible?: boolean;
  /**
   * Deployment POSTURE: may a kernel write land with nobody watching (D-002)?
   *
   * False is the normal state and does NOT mean learning is off — `raisy_62` still routes,
   * `raisy_73` still authors the kernel edit, and the proposal is still shown for review
   * by `KernelFixRunPanel`. Only the unattended write is withheld.
   */
  unattendedKernelWrite?: boolean;
  /** The memory contradicted existing lines, so it went to the raisy_73 path (D-003). */
  escalateTo73?: boolean;
  contradicts?: string[];
  refusalDetail?: Record<string, unknown>;
  memory?: {
    kernelKey: string;
    versionNumber: number | null;
    /** True when the write was redirected to a private entity-scoped kernel (D-008). */
    redirectedToPrivate: boolean;
    entityId: string | null;
    memoryLine: string;
    memoryLineCount: number;
  };
}

export interface VerdictResult {
  rewriteCallId: string;
  exchangeId: string;
  exchangeVersion: number;
  verdict: "accepted" | "declined";
  learning: LearningOutcome;
  /**
   * Mirror of `learning.written` — what HAS happened, not what will.
   *
   * Since D-019 this is always false at verdict time: the verdict no longer learns
   * anything itself. One caller does the learning, and it runs after this response.
   * `learning.chainEligible` is the field that says what is going to happen.
   */
  learningQueued: boolean;
  /** Set when a DECLINE started a chain here — accepts start theirs at compose. */
  patchRunId?: string | null;
  chainStarted?: boolean;
}

export interface ExchangeEntry {
  rewriteCallId: string;
  exchangeId: string;
  exchangeVersion: number;
  markedSpan: string;
  markedField: string;
  userFeedback: string;
  rewrittenSpan: string | null;
  verdict: string;
  status: string;
  errorMessage: string | null;
  latencyMs: number | null;
  timestamp: string | null;
}

// ── Helper ─────────────────────────────────────────────────────────

async function apiCall<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const headers = await AuthUtils.getAuthHeaders();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: { ...headers, ...options.headers },
  });
  return res.json();
}

// ── Endpoints ──────────────────────────────────────────────────────

/** T0201 — span → kernel-candidate map. Deterministic; no LLM call behind it. */
export async function getCallAttribution(
  callId: string,
  entityId?: string,
): Promise<ApiResponse<CallAttribution>> {
  return apiCall(`/api/v1/bot/call/${callId}/attribution`, {
    method: "POST",
    body: JSON.stringify({ entity_id: entityId ?? null }),
  });
}

/** One output version in a call's lineage (T0819). */
export interface OutputVersion {
  callId: string;
  version: number;
  isComposed: boolean;
  isOriginal: boolean;
  parentCallId: string | null;
  timestamp: string | null;
  status: string | null;
  appliedSpanCount: number;
  patchRunId?: string | null;
  /** The CLEAN deliverable for this version — never carries markers. */
  output: unknown;
  /** True when the bot fenced this version's output, so provenance is available. */
  hasMarkers: boolean;
  /**
   * T0835 — the one version of this lineage that currently counts.
   *
   * Read off `bot_call_logs.is_selected`, never inferred from position. "The newest one"
   * is exactly the assumption `selectOutputVersion` exists to break.
   */
  isSelected: boolean;
}

/**
 * P135 T1362 row 7 (half A) — ONE version, AS THE LISTING SEEDS IT.
 *
 * ⚠️ DELIBERATELY NARROWER THAN `OutputVersion`, and the omissions are the design decision
 * rather than an oversight: **there is no `output` here.** `OutputVersionLineage` ships every
 * version's full text, which is right for ONE call and wrong for a PAGE of them — a 20-row
 * history would multiply its payload by the lineage size. The text of a version that is not on
 * screen still arrives from `getOutputVersions`, which stays as revalidate.
 *
 * TypeScript makes that structural: `OutputVersion` is **not** assignable from this, so a pane
 * that renders text cannot be handed a seed by mistake.
 */
export interface SeedVersion {
  callId: string;
  version: number;
  isComposed: boolean;
  parentCallId: string | null;
  timestamp: string | null;
  status: string | null;
  /** Read off `bot_call_logs.is_selected`, never inferred from position (T0835). */
  isSelected: boolean;
}

/**
 * The version bar's seed, as `POST /bot/call-history` returns it when `includeLineage` is true.
 *
 * The bar needs the LIST (the chips, which one is selected); the pane needs the TEXT, and the
 * text is not here. See `SeedVersion`.
 */
export interface VersionLineageSeed {
  versions: SeedVersion[];
  /** The version to mark selected. Falls back to the NEWEST when nothing is flagged — the same
   *  rule `OutputVersionLineage` uses, and never to v1, because reverting a user to the original
   *  on an un-migrated database looks exactly like data loss. */
  selectedCallId: string | null;
  count: number;
}

export interface OutputVersionLineage {
  rootCallId: string;
  requestedCallId: string;
  /**
   * The version to open. Falls back to the NEWEST when nothing is selected, which means
   * migration 132 has not reached this lineage — never to v1, because reverting a user to
   * the original on an un-migrated database looks exactly like data loss.
   */
  selectedCallId: string | null;
  versions: OutputVersion[];
  count: number;
  /** True when older versions exist outside the query window and are not listed. */
  partial: boolean;
}

/**
 * T0819 — every output version in this call's lineage, v1 first.
 *
 * Any version may be passed and the same complete set comes back: the server walks UP to
 * the original before walking down, so selecting v3 and re-fetching does not lose v1.
 */
export async function getOutputVersions(
  callId: string,
): Promise<ApiResponse<OutputVersionLineage>> {
  return apiCall(`/api/v1/bot/call/${callId}/versions`, { method: "GET" });
}

/**
 * T0835 — make one version the selected one for its lineage.
 *
 * Clear-then-set in one transaction on the server, so the lineage is never left with two
 * selected versions or none. Returns the refreshed lineage: one round trip writes and
 * re-renders.
 *
 * ⚠️ The only call in this file that mutates a `bot_call_logs` row's own columns. Every
 * other write here appends a row. `is_selected` is a pointer into the lineage, not a fact
 * about the call, so it is the one thing that is allowed to move.
 */
export async function selectOutputVersion(
  callId: string,
): Promise<ApiResponse<OutputVersionLineage>> {
  return apiCall(`/api/v1/bot/call/${callId}/versions/select`, { method: "POST" });
}

/**
 * T0300 — mark + feedback → rewritten span. FAST tier, target p90 ≤ 3 s.
 *
 * Returns a CANDIDATE. Nothing is applied until the user accepts, and even then only
 * in the view — no kernel is written anywhere in Phase 2.
 *
 * 422 means the span could not be located or was ambiguous; that is a legitimate
 * outcome with a defined fallback, not a server fault.
 */
export async function rewriteSpan(
  callId: string,
  payload: {
    spanText: string;
    userFeedback: string;
    field?: string;
    entityId?: string;
    /**
     * T1368 / IN-08 — the conversation BEFORE this draft, oldest first. Built by the caller
     * (`mail-review` adapts the thread it already reads, via `priorTurnsFromThread`), so this
     * adds no fetch; the BE owns the block's shape, its caps and its entity guard.
     */
    priorTurns?: Array<{ direction?: "inbound" | "outbound"; sender?: string; date?: string; text: string }> | null;
    /** T0803 — BD tier of the matter at fault, e.g. 120 for Tone. */
    dimension?: number | null;
    /**
     * The caller's own offset for the span inside the field. VERIFIED server-side,
     * never searched for — marking the second of two identical sentences is only
     * correct if the offset travels with it.
     */
    spanStart?: number | null;
  },
): Promise<ApiResponse<SpanRewriteResult>> {
  return apiCall(`/api/v1/bot/call/${callId}/span-rewrite`, {
    method: "POST",
    body: JSON.stringify({
      spanText: payload.spanText,
      userFeedback: payload.userFeedback,
      field: payload.field ?? null,
      spanStart: payload.spanStart ?? null,
      dimension: payload.dimension ?? null,
      entity_id: payload.entityId ?? null,
      // T1368 — omitted entirely when there is nothing to send, so a caller that has no
      // thread behaves exactly as before ("priorTurns: null" would be a claim, not an absence).
      ...(payload.priorTurns?.length ? { priorTurns: payload.priorTurns } : {}),
    }),
  });
}

/**
 * T0804 — apply accepted spans to produce the `v+1` output. **No model call.**
 *
 * Every id passed must already carry an `accepted` verdict. A 422 means a legitimate
 * refusal — a pending verdict, overlapping spans, or an offset that no longer matches
 * the parent's text — each a case where applying the edit anyway would produce a
 * plausible-looking wrong deliverable.
 */
export async function composeOutputVersion(
  callId: string,
  payload: {
    rewriteCallIds: string[];
    entityId?: string;
    patchRunId?: string | null;
    /**
     * T1354 — slots the user filled by hand. Applied in the SAME pass as the accepted
     * rewrites, so marking one span and filling two slots is ONE version, not three.
     *
     * Unlike a rewrite these carry their text, because the value is a fact the user holds
     * and the model was never asked.
     */
    fills?: SlotFillPayload[];
    /**
     * P237 R2-7 — whole fields the owner retyped directly ("edit it yourself"). Applied in
     * the SAME pass; a fieldEdit REPLACES the whole field text, and the BE refuses an
     * overlap with a span edit or fill on the same field.
     */
    fieldEdits?: FieldEditPayload[];
    /** Run the background 62→73 fan-out after composing. Omitted = the BE default (true). */
    startChain?: boolean;
  },
): Promise<ApiResponse<ComposedVersion>> {
  return apiCall(`/api/v1/bot/call/${callId}/compose-version`, {
    method: "POST",
    body: JSON.stringify({
      rewriteCallIds: payload.rewriteCallIds,
      fills: payload.fills ?? [],
      ...(payload.fieldEdits ? { fieldEdits: payload.fieldEdits } : {}),
      entity_id: payload.entityId ?? null,
      patchRunId: payload.patchRunId ?? null,
      ...(payload.startChain !== undefined ? { startChain: payload.startChain } : {}),
    }),
  });
}

/**
 * P237 R2-7 — one whole field the owner retyped directly. No offsets: the text replaces
 * the field, and the BE verifies the field exists and the text actually differs.
 */
export interface FieldEditPayload {
  field: string;
  text: string;
}

/**
 * T1354 — one unfilled `[SLOT]` a person typed a value into.
 *
 * ⚠️ `spanStart`/`spanEnd` are offsets into the CLEAN field text — the space the reader
 * selects in, and the same one the BE verifies against. They are NOT re-derived from
 * `text` on the server, so a stale pair is refused rather than landed in the wrong slot.
 */
export interface SlotFillPayload {
  /** The text to put in the slot — the words a reader will see. */
  value: string;
  spanStart: number;
  spanEnd: number;
  /** The placeholder as rendered, e.g. `[START_DATE]`. Verified server-side, not trusted. */
  slot?: string;
  field?: string;
}

/**
 * T0302 / T0303 — accept or decline. Both verdicts are recorded; neither writes a kernel.
 *
 * 🔁 D-135-026-R — THIS CALL DOES NOT SEND AN ENTITY, ON PURPOSE. An earlier revision of this
 * session added `entityId` to the body to "fix" an unscoped decline path. That fix rested on a
 * diagnosis that turned out to be WRONG, and the field was inert besides:
 *
 *   - the route already sources the entity from the EXCHANGE ROW, not from this payload —
 *     `span_feedback_manager.record_verdict` forwards `data.get("entityId")`, and
 *     `span_feedback_service.record_verdict` returns `"entityId": variables.get("entity_id")`.
 *     Both lines date to 2026-09-01 (`436705048`), long before this was raised.
 *   - `VerdictRequest` (BE) declares only `rewriteCallId` and `verdict`, so a pydantic model that
 *     ignores unknown keys would have dropped the extra field silently anyway.
 *   - verified on DEV: the decline response carries a real
 *     `entityId: raisy_ent_03640_20260902034631`, so the chain was never unscoped.
 *
 * A client-supplied scope should NOT outrank the server-written row (that is an authorization
 * question), so the honest state is: no entity here, and none needed. See D-135-026-R in
 * `project135_raisy_memory_system.md`.
 */
export async function recordSpanVerdict(
  callId: string,
  rewriteCallId: string,
  verdict: "accepted" | "declined",
): Promise<ApiResponse<VerdictResult>> {
  return apiCall(`/api/v1/bot/call/${callId}/span-rewrite/verdict`, {
    method: "POST",
    body: JSON.stringify({ rewriteCallId, verdict }),
  });
}

/**
 * T0401 / T0404 — `62_single_kernel_most_relevant`.
 *
 * Candidates + feedback → exactly ONE kernelKey. Writes nothing, and does not decide
 * whether anything may be written: `belowFloor` reports which side of the confidence
 * floor the answer fell on, and the floor itself is provisional until T0107.
 *
 * ⚠️ NO FE CALLER since D-031 deleted `UnmarkedFeedbackPanel` (2026-09-13) — the vague
 * complaint now goes through Reject → raisy_61. Kept because the endpoint is live and
 * still routes on the BE side (the marked path in `kernel_fix_chain` runs the same 62),
 * so this is an unused client, not a broken one. Delete it if a second release passes
 * without a caller.
 */
export async function mostRelevantKernel(payload: {
  candidateKernelKeys: string[];
  userFeedback: string;
  entityId?: string;
  exchangeId?: string;
  botId?: string;
}): Promise<ApiResponse<RouterResult>> {
  return apiCall("/api/v1/kernel/most-relevant", {
    method: "POST",
    body: JSON.stringify({
      candidateKernelKeys: payload.candidateKernelKeys,
      userFeedback: payload.userFeedback,
      entity_id: payload.entityId ?? null,
      exchangeId: payload.exchangeId ?? null,
      botId: payload.botId ?? null,
    }),
  });
}

export interface RouterResult {
  kernelKey: string;
  /** "BD120.2 Formal Corporate" — display only. */
  label: string;
  confidence: number;
  reason: string;
  /** True when the router is not confident enough for anything to be written on it. */
  below_floor: boolean;
  confidence_floor: number;
  /** The floor is a placeholder until T0107 derives it. Shown to the user as such. */
  confidence_floor_provisional: boolean;
  candidates: string[];
  candidates_without_index_block: string[];
  latencyMs: number;
}

/**
 * T0807 — one kernel the background run proposed a change to.
 *
 * `proposedContent` is REBUILT at read time by re-applying the `73` call's delta ops to
 * the kernel's current content, so the diff is against what the kernel says now rather
 * than a snapshot. Ops that no longer apply come back in `skippedOps` instead of
 * disappearing — a proposal built from half-stale ops is not the same proposal.
 *
 * **Nothing here is in force** (D-017). Accepting one means calling `applyKernelPatch`
 * with `proposedContent` and the same `patchRunId`.
 */
export interface KernelProposal {
  callId: string;
  kernelKey: string | null;
  kernelName?: string;
  exchangeId: string | null;
  /**
   * T0846 — every mark this ONE proposal answers.
   *
   * A proposal used to be per-mark. It is now per-KERNEL: when several corrections route
   * to the same kernel they are patched together, so one proposal can carry two or three
   * exchanges. Length > 1 is the signal to tell the reviewer that accepting this covers
   * all of them — the previous behaviour produced N rival proposals and applying one
   * silently discarded the rest.
   */
  exchangeIds?: string[];
  deltaOpCount: number;
  /**
   * D-135-025 — the plain-language rule this proposal establishes, written by the patcher
   * to be read ALONE by a non-engineer (no kernel keys, no BD codes, no field names).
   *
   * 👤: *"we cant just say this … but the actual information or actual learning must be
   * showed"*. `null` when the patcher judged the ops to be plumbing and declined to invent
   * a rule — the panel then renders no sentence, which is the honest render.
   */
  ruleLearned?: string | null;
  /**
   * D-135-025 — `true` ONLY when the patcher answered and returned an EMPTY `rule_learned`: it
   * looked at the change and judged there to be no rule a person could restate (plumbing).
   *
   * ⚠️ Distinct from `ruleLearned === null` with this false, which means we have no answer at all
   * (an older row, or a contract not yet applied). The FTUE persona forbids the surface from
   * stating the second as if it were the first — one is a judgement we can quote, the other is an
   * absence we can only report.
   */
  ruleOmitted?: boolean;
  currentVersion?: number;
  currentContent: string | null;
  proposedContent: string | null;
  appliedOps: { index: number; op: string; rationale: string }[];
  skippedOps: { index: number; op: string; error: string; rationale: string }[];
  /** False when every op was stale — an empty diff, said out loud rather than rendered. */
  reviewable?: boolean;
  /** True when this run's stamp is already on the kernel's current version. */
  applied: boolean;
  /**
   * IN-21 — the kernel's accept/reject ratio across past proposals (the confidence
   * score). `null` means no history yet — a different claim from "0% accepted".
   */
  confidenceScore?: number | null;
  approvals?: number;
  rejections?: number;
  totalDecisions?: number;
  /**
   * T0843 — may THIS viewer commit this proposal?
   *
   * Mirrors `_can_modify_kernel`, the predicate `/kernel/apply-patch` actually enforces:
   * global kernels are admin/service only, entity-scoped ones need `editor` on that
   * entity. The panel used to render Apply unconditionally and let the 403 explain
   * itself — a button that tells you it was never yours only after you press it.
   *
   * Undefined on rows from a backend that predates this field; treated as "allowed" so
   * an older BE does not silently hide every button.
   */
  canApply?: boolean;
  /**
   * T0842 — the human decision on this proposal, or null while undecided.
   *
   * `null` genuinely means "nobody has looked", which is only a meaningful state now that
   * rejecting is recordable at all. Before this, "rejected" and "never opened" were the
   * same absence.
   */
  verdict?: "approved" | "rejected" | null;
  verdictReason?: string | null;
  decidedBy?: string | null;
  decidedAt?: string | null;
  scope?: string;
  entityId?: string | null;
  status: string;
  errorMessage: string | null;
  latencyMs: number | null;
  timestamp: string | null;
  reason?: string;
}

/** The routing decision behind each proposal — which kernel `62` picked, and how sure. */
export interface RouterCallSummary {
  callId: string;
  botId: string;
  kernelKey: string | null;
  confidence: number | null;
  belowFloor: boolean | null;
  exchangeId: string | null;
  status: string;
  errorMessage: string | null;
  latencyMs: number | null;
  timestamp: string | null;
}

/** D-135-022 — one mark that produced no change, and the reason it did not. */
export interface RunRefusal {
  exchangeId: string | null;
  /** The chain's own stage (`custom_target`, `structural_target`, `below_floor`, …). */
  stage: string | null;
  /** The sentence for the PERSON. Never an env var, a task id or a kernel key. */
  reasonUser: string | null;
  /** The diagnostic — for the hover only, because the log is not the copy (P135 IN-10). */
  reason: string | null;
  timestamp: string | null;
}

export interface PatchRunProposals {
  patchRunId: string;
  proposals: KernelProposal[];
  routerCalls: RouterCallSummary[];
  /**
   * D-135-022 — marks this run looked at and deliberately did NOT propose a change for,
   * each with the reason the chain wrote at the time.
   *
   * ⚠️ DISTINCT FROM "still running" AND FROM "died". A mark absent from this list was
   * never reached, or it produced a proposal — so the panel must not render "nothing was
   * learned" from an empty proposal list alone, which is exactly what it used to do.
   */
  refusals: RunRefusal[];
  proposalCount: number;
  appliedCount: number;
  callCount: number;

  /**
   * T0810 — did this run finish, or did it die?
   *
   * Without these, a run whose process was killed after one mark looks identical to one
   * that legitimately proposed nothing — the silent-failure shape R-01c exists to
   * prevent. `expectedMarkCount` comes from the journal row written before the run's
   * background task was ever queued (D-020).
   *
   * `complete: null` means the question could not be answered: no journal row carries
   * this id. Say that, rather than guessing either way.
   */
  expectedMarkCount: number | null;
  routedMarkCount: number;
  complete: boolean | null;
  livenessNote: string;
}

/**
 * T0807 — read back one background kernel-fix run.
 *
 * POST rather than GET because `entity_id` decides which VERSION each proposal is diffed
 * against: omitting it silently changes the answer instead of failing, so it belongs in a
 * body where it is explicit.
 */
/**
 * T0842 — record a decision about ONE proposal.
 *
 * This does NOT apply anything. Applying still goes through `applyKernelPatch` →
 * `/kernel/apply-patch`, the one path where `guard_kernel_write` runs (D-017). This is the
 * decision itself, which previously had nowhere to live: the panel offered Apply-or-ignore,
 * so "reviewed and rejected" looked exactly like "never opened" and `73` could re-propose
 * the same rejected edit forever.
 */
export async function recordProposalVerdict(payload: {
  patchCallId: string;
  verdict: "approved" | "rejected";
  reason?: string;
  patchRunId?: string;
  kernelKey?: string;
  entityId?: string;
}): Promise<ApiResponse<{ patchCallId: string; verdict: string; decidedAt: string }>> {
  return apiCall("/api/v1/kernel/patch-run/verdict", {
    method: "POST",
    body: JSON.stringify({
      patchCallId: payload.patchCallId,
      verdict: payload.verdict,
      reason: payload.reason ?? null,
      patchRunId: payload.patchRunId ?? null,
      kernelKey: payload.kernelKey ?? null,
      entity_id: payload.entityId ?? null,
    }),
  });
}

/**
 * T0503 / IN-10 — the memory block on a kernel: what the system has LEARNED and kept.
 *
 * ⚠️ This endpoint has existed since T0503 and had **no reader anywhere in the app**, so
 * "what we learned" was already retrievable and merely not shown. The toast says the
 * preference was acknowledged; this is the durable half — the lines still on the kernel
 * tomorrow, which is what "we won't forget" has to mean to be an honest claim.
 */
export interface KernelMemoryLine {
  text: string;
  exchangeId: string | null;
  writtenAt: string | null;
}

export interface KernelMemory {
  kernelKey: string;
  versionNumber: number | null;
  entityId: string | null;
  scope: string | null;
  memoryLines: KernelMemoryLine[];
  count: number;
  cap: number;
}

export async function getKernelMemory(
  kernelKey: string,
  entityId?: string,
): Promise<ApiResponse<KernelMemory>> {
  // `entity_id` decides WHICH version's memory is read (a private copy belongs to one
  // entity), so it is sent explicitly whenever the caller knows it.
  const qs = entityId ? `?entity_id=${encodeURIComponent(entityId)}` : "";
  return apiCall(
    `/api/v1/kernel/${encodeURIComponent(kernelKey)}/memory${qs}`,
    { method: "GET" },
  );
}

/**
 * D-029 — undo what auto-learn wrote for one run, on one kernel.
 *
 * 🚨 THE SAFETY NET, NOT A CONVENIENCE. Auto-learn applies `73`'s patch to every kernel
 * including SHARED ones, so nothing reviews the write before it lands — reversibility is
 * what stands in for review. The persona asks for exactly that: "every action auditable
 * and **reversible**".
 *
 * Restores the version before the auto-applied one, as a NEW version. Nothing is deleted.
 *
 * ⚠️ 409 is a real answer, not a failure: the kernel moved on since auto-learn wrote it
 * (reverting would discard someone else's later edit), the run was never applied, or there
 * is no earlier version to restore. Show `detail` to the user — the kernel is untouched.
 */
export async function revertAutoLearnPatch(payload: {
  kernelKey: string;
  patchRunId: string;
  entityId?: string;
}): Promise<
  ApiResponse<{
    reverted: boolean;
    kernelKey: string;
    revertedVersion: number;
    restoredFromVersion: number;
    newVersion: number;
  }>
> {
  return apiCall("/api/v1/kernel/patch-run/revert", {
    method: "POST",
    body: JSON.stringify({
      kernelKey: payload.kernelKey,
      patchRunId: payload.patchRunId,
      entity_id: payload.entityId ?? null,
    }),
  });
}

/** T0844 — one row per background kernel-fix run. Aggregates only; no diffs. */
export interface PatchRunSummary {
  patchRunId: string;
  startedAt: string | null;
  lastCallAt: string | null;
  callCount: number;
  /** How many `73` calls the run made — i.e. how many kernels it proposed against. */
  patchCallCount: number;
  kernelKeys: string[];
  entityId: string | null;
  botId: string | null;
  failedCallCount: number;
}

export interface PatchRunList {
  runs: PatchRunSummary[];
  runCount: number;
  /** The lookback used. Shown, so a short list is never mistaken for "no runs". */
  windowDays: number;
  truncated: boolean;
}

/**
 * T0844 — the review queue's inbox.
 *
 * Before this, a run was reachable only from the output that started it: the panel lives
 * inside `MarkableOutput` and needs the `patchRunId` `/compose-version` returned. Navigate
 * away and the run was unreachable from the UI while its proposals still existed.
 *
 * POST for the same reason as the proposals read: `entity_id` changes the answer and is
 * access-checked server-side, so it belongs in a body rather than a droppable query param.
 */
export async function getPatchRuns(payload: {
  entityId?: string;
  limit?: number;
  days?: number;
} = {}): Promise<ApiResponse<PatchRunList>> {
  return apiCall("/api/v1/kernel/patch-runs", {
    method: "POST",
    body: JSON.stringify({
      entity_id: payload.entityId ?? null,
      limit: payload.limit ?? 50,
      days: payload.days ?? 30,
    }),
  });
}

export async function getPatchRunProposals(payload: {
  patchRunId: string;
  entityId?: string;
  limit?: number;
}): Promise<ApiResponse<PatchRunProposals>> {
  return apiCall("/api/v1/kernel/patch-run/proposals", {
    method: "POST",
    body: JSON.stringify({
      patchRunId: payload.patchRunId,
      entity_id: payload.entityId ?? null,
      limit: payload.limit ?? 500,
    }),
  });
}

/**
 * T0810 — finish the marks a stopped run never routed.
 *
 * Runs only the difference, not the whole run: re-running everything would mint duplicate
 * `73` calls under the same id and make the run's own record ambiguous, which is the thing
 * `patchRunId` exists to prevent.
 */
export async function resumePatchRun(payload: {
  patchRunId: string;
  entityId?: string;
  limit?: number;
}): Promise<ApiResponse<{ patchRunId: string; queued: number; marks: (string | null)[] }>> {
  return apiCall("/api/v1/kernel/patch-run/resume", {
    method: "POST",
    body: JSON.stringify({
      patchRunId: payload.patchRunId,
      entity_id: payload.entityId ?? null,
      limit: payload.limit ?? 500,
    }),
  });
}

/** T0303 — the full v+1 chain for one original call. */
export async function getExchangeChain(
  callId: string,
): Promise<ApiResponse<{ parentCallId: string; exchanges: ExchangeEntry[]; count: number }>> {
  return apiCall(`/api/v1/bot/call/${callId}/exchanges`);
}
